"use client";

import React from "react";
import { MatchupHeader } from "fansunited-sports-ui";

interface HeaderColors {
    gradientFrom: string;
    gradientTo: string;
    textColor: string;
}

interface TeamEntity {
    imageUrl: string;
    /** Custom image element (e.g. TeamLogo) — takes precedence over imageUrl */
    image?: React.ReactNode;
    name: string;
    subtitle?: string;
    href?: string;
}

interface BrandedMatchHeaderProps {
    leftTeam: TeamEntity;
    rightTeam: TeamEntity;
    centerContent: React.ReactNode;
    colors?: HeaderColors;
    backgroundImageUrl?: string;
    /** Small meta line rendered above the matchup (e.g. competition + round info) */
    topMeta?: React.ReactNode;
    /**
     * Optional breadcrumb element rendered at the top of the hero, above the matchup row.
     * Pass an awaited `<Breadcrumbs ... />` from a server component.
     */
    breadcrumbs?: React.ReactNode;
}

export function BrandedMatchHeader({
    leftTeam,
    rightTeam,
    centerContent,
    colors,
    backgroundImageUrl,
    topMeta,
    breadcrumbs,
}: BrandedMatchHeaderProps) {
    // Fall back to the tenant's header slot tokens when no per-section colours are provided.
    // The hex values are CSS-level fallbacks for the var() lookups, kept as a last-resort safety net.
    const gradientFrom = colors?.gradientFrom || "var(--header-gradient-from, #1f2937)";
    const gradientTo = colors?.gradientTo || "var(--header-gradient-to, #374151)";
    const textColor = colors?.textColor || "var(--header-text, #ffffff)";
    const textColorMuted = colors?.textColor
        ? `color-mix(in srgb, ${colors.textColor} 70%, transparent)`
        : "color-mix(in srgb, var(--header-text, #ffffff) 70%, transparent)";
    const bgImageUrl = backgroundImageUrl;

    // Build background style - always include gradient, with optional image overlay
    const backgroundStyle = bgImageUrl
        ? `url(${bgImageUrl}), linear-gradient(to right, ${gradientFrom}, ${gradientTo})`
        : `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`;

    return (
        <div
            className="p-3 md:p-6"
            style={
                {
                    background: backgroundStyle,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    color: textColor,
                    // Set CSS custom properties for text colors so they can be used in child typography
                    "--header-text-color": textColor,
                    "--header-text-color-muted": textColorMuted,
                } as React.CSSProperties
            }
        >
            <div className="max-w-350 mx-auto p-4 md:py-8">
                {breadcrumbs ? <div className="mb-6 md:mb-8">{breadcrumbs}</div> : null}
                {topMeta && <div className="mb-3 md:mb-4">{topMeta}</div>}
                {/* Mobile: Stacked layout - names under logos */}
                <div className="block md:hidden">
                    <MatchupHeader
                        centerContent={centerContent}
                        leftEntity={{
                            image: leftTeam.image,
                            imageUrl: leftTeam.imageUrl,
                            name: leftTeam.name,
                            subtitle: leftTeam.subtitle,
                            href: leftTeam.href,
                            styles: {
                                imageBorderRadius: "md",
                                imageSize: "md",
                                typography: {
                                    name: "text-sm font-semibold text-center line-clamp-2 [color:var(--header-text-color)]",
                                    subtitle: "text-xs text-center [color:var(--header-text-color-muted)]",
                                },
                            },
                        }}
                        rightEntity={{
                            image: rightTeam.image,
                            imageUrl: rightTeam.imageUrl,
                            name: rightTeam.name,
                            subtitle: rightTeam.subtitle,
                            href: rightTeam.href,
                            styles: {
                                imageBorderRadius: "md",
                                imageSize: "md",
                                typography: {
                                    name: "text-sm font-semibold text-center line-clamp-2 [color:var(--header-text-color)]",
                                    subtitle: "text-xs text-center [color:var(--header-text-color-muted)]",
                                },
                            },
                        }}
                        styles={{
                            centerTypography: "text-sm font-bold [color:var(--header-text-color)]",
                            gap: "2",
                        }}
                        variant="stacked"
                    />
                </div>

                {/* Desktop: Inline layout - names beside logos */}
                <div className="hidden md:block">
                    <MatchupHeader
                        centerContent={centerContent}
                        leftEntity={{
                            image: leftTeam.image,
                            imageUrl: leftTeam.imageUrl,
                            name: leftTeam.name,
                            subtitle: leftTeam.subtitle,
                            href: leftTeam.href,
                            styles: {
                                imageBorderRadius: "md",
                                imageSize: "2xl",
                                typography: {
                                    name: "text-2xl font-semibold mr-2 [color:var(--header-text-color)]",
                                    subtitle: "text-sm mr-2 [color:var(--header-text-color-muted)]",
                                },
                            },
                        }}
                        rightEntity={{
                            image: rightTeam.image,
                            imageUrl: rightTeam.imageUrl,
                            name: rightTeam.name,
                            subtitle: rightTeam.subtitle,
                            href: rightTeam.href,
                            styles: {
                                imageBorderRadius: "md",
                                imageSize: "2xl",
                                typography: {
                                    name: "text-2xl font-semibold ml-2 [color:var(--header-text-color)]",
                                    subtitle: "text-sm ml-2 [color:var(--header-text-color-muted)]",
                                },
                            },
                        }}
                        styles={{
                            centerTypography: "text-lg font-bold [color:var(--header-text-color)]",
                        }}
                        variant="inline"
                    />
                </div>
            </div>
        </div>
    );
}
